import React, { useState } from "react";
import mammoth from "mammoth";
import { GoogleGenerativeAI } from "@google/generative-ai";

import * as pdfjsLib from "pdfjs-dist";
import * as resumeModel from "../models/ResumeParserModel";
import { useAuth } from "../context/AuthContext";
// ✅ Correct worker setup for pdfjs-dist v3+
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url
).toString();


// Gemini init
const genAI = new GoogleGenerativeAI("AIzaSyABiqSj6MwjoC0kDJVYEenBGLpG1jvU7mA");
const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });

export default function FileGemini() {
    const { state } = useAuth();
    let user_data = state.user_data;
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    // ---------- Helpers ----------

    const fileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = (error) => reject(error);
    });

    const extractTextFromPdf = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        let textContent = "";
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const text = await page.getTextContent();
            textContent += text.items.map((s) => s.str).join(" ") + "\n";
        }
        return textContent.trim();
    };

    const extractTextFromDocx = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const { value } = await mammoth.extractRawText({ arrayBuffer });
        return value.trim();
    };

    const ocrPdfWithGemini = async (file) => {
        const base64 = await fileToBase64(file);

        const prompt = `Extract structured resume details in strict JSON:{
            "name": "",
            "email": "",
            "phone": "",
            "location": "",
            "education": [],
            "experience": [],
            "skills": []
        }`;

        const result = await model.generateContent([{inlineData: { data: base64, mimeType: file.type },},{ text: prompt },]);

        return (await result.response).text();
    };

    function normalizeGeminiResponse(responseText) {
        try {
            const cleaned = responseText
                .replace(/```json/g, "")
                .replace(/```/g, "")
                .trim();
            const parsed = JSON.parse(cleaned);

            // ✅ Ensure schema consistency
            return {
                name: parsed.name || "",
                email: parsed.email || "",
                phone: parsed.phone || "",
                location: parsed.location || "",
                education: parsed.education || [],
                experience: parsed.experience || [],
                skills: parsed.skills || [],
            };
        } catch (err) {
            console.error("Failed to parse Gemini response", err);
            // fallback blank schema
            return {
                name: "",
                email: "",
                phone: "",
                location: "",
                education: [],
                experience: [],
                skills: [],
            };
        }
    }

    // ---------- Retry helper ----------
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    async function safeGenerateContent(prompt, retries = 3) {
        let attempt = 0;
        let delay = 2000; // start with 2 sec

        while (attempt < retries) {
            try {
                return await model.generateContent(prompt);
            } catch (err) {
                if (err.status === 429 || err.message?.includes("RESOURCE_EXHAUSTED")) {
                    console.warn(`429 received, retrying in ${delay / 1000}s...`);
                    await sleep(delay);
                    attempt++;
                    delay *= 2; // exponential backoff
                } else {
                    throw err; // other errors → don't retry
                }
            }
        }
        throw new Error("Quota exceeded after retries");
    }

    // ---------- Main parsing ----------
    const parseResume = async (file) => {
        try {
            let text = "";

            if (file.type === "application/pdf") {
                text = await extractTextFromPdf(file);
                if (!text) {
                    console.warn("No text found in PDF, using Gemini OCR...");
                    text = await ocrPdfWithGemini(file);
                }
            } else if (
                file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                file.name.endsWith(".docx")
            ) {
                text = await extractTextFromDocx(file);
            } else {
                throw new Error("Unsupported file type: " + file.type);
            }

            const result = await safeGenerateContent([
                {
                    text: `
                    Extract structured resume details in strict JSON:
                    {
                        "name": "",
                        "email": "",
                        "phone": "",
                        "location": "",
                        "education": [],
                        "experience": [],
                        "skills": []
                    }
                    Resume content:
                    ${text}`,
                },
            ]);

            const jsonText = (await result.response).text();
            const json = normalizeGeminiResponse(jsonText);

            return { fileName: file.name, data: json };
        } catch (err) {
            console.error("Failed to parse resume:", err);

            return {
                fileName: file.name,
                data: {
                    name: "",
                    email: "",
                    phone: "",
                    location: "",
                    education: [],
                    experience: [],
                    skills: [],
                },
                error: err.message || "Unknown error",
            };
        }
    };
    // ---------- Handlers ----------
    const handleUploadOld = async (event) => {
        const files = Array.from(event.target.files);
        if (!files.length) return;

        setLoading(true);

        const parsedResults = [];
        for (const file of files) {
            const res = await parseResume(file);
            parsedResults.push(res);
        }

        setResults((prev) => [...prev, ...parsedResults]);
        setLoading(false);
        console.log("All parsed results:", parsedResults);
    };
    const handleUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (!files.length) return;

        setLoading(true);

        // Run parsing for all resumes in parallel
        const parsedResults = await Promise.all(files.map(file => parseResume(file)));

        setResults((prev) => [...prev, ...parsedResults]);
        setLoading(false);
        console.log("All parsed results:", parsedResults);
    };


    const sendToBackend = async () => {
        try {
            const response = await resumeModel.create(user_data.user_token, { resumes: results.map((r) => r.data) });
            if(response.status){
                alert(response.message);
            }
            //alert("Resumes saved successfully!");
        } catch (err) {
            console.error("Save error:", err);
            alert("Error saving resumes.");
        }
    };

    // ---------- UI ----------
    return (
        <div className="p-4">
            <h2 className="text-xl font-bold mb-2">Resume Uploader</h2>

            <input type="file" accept=".pdf,.doc,.docx" multiple onChange={handleUpload}/>

            {loading && <p className="text-blue-600">Processing resumes...</p>}

            <ul className="mt-4 space-y-2">
            {results.map((res, idx) => (
                <li key={idx} className="p-2 border rounded bg-gray-50 text-sm whitespace-pre-wrap" >
                    <strong>{res.fileName}</strong>
                    <pre>{JSON.stringify(res.data, null, 2)}</pre>
                </li>
            ))}
            </ul>

            {results.length > 0 && (
                <button
                onClick={sendToBackend}
                className="mt-4 px-4 py-2 bg-green-600 text-white rounded"
                >
                Save All to Backend
                </button>
            )}
        </div>
    );
}